import { useCallback, useEffect, useRef, useState } from 'react'

/* A real DOM scrollbar for the form scroll container (.form-shell).
 *
 * macOS's "Show scroll bars: When scrolling" preference overrides any
 * ::-webkit-scrollbar styling, so the native bar can't be made reliably
 * always-on. The fix: hide the native bar entirely (CSS) and render this
 * custom track + thumb that we drive from the container's scroll metrics.
 *
 * - Track: always visible WHEN the container is scrollable; hidden when it all
 *   fits (content <= viewport).
 * - Thumb height = clientHeight / scrollHeight (of the real scroller).
 * - Thumb position tracks scrollTop. Draggable; clicking the track jumps.
 * - Recalculates on scroll, on resize, and on content size changes
 *   (ResizeObserver) — e.g. adding another provider/owner row.
 */
export default function CustomScrollbar({ targetRef }) {
  const trackRef = useRef(null)
  const thumbRef = useRef(null)
  const [thumb, setThumb] = useState({ visible: false, height: 0, top: 0 })

  const recalc = useCallback(() => {
    const el = targetRef.current
    const track = trackRef.current
    if (!el || !track) return
    const { scrollTop, scrollHeight, clientHeight } = el
    const trackH = track.clientHeight
    const scrollable = scrollHeight > clientHeight + 1 && trackH > 0
    if (!scrollable) {
      setThumb((t) => (t.visible ? { ...t, visible: false } : t))
      return
    }
    const thumbH = Math.max(30, Math.round(trackH * (clientHeight / scrollHeight)))
    const maxScroll = scrollHeight - clientHeight
    const maxThumbTop = trackH - thumbH
    const top = maxScroll > 0 ? (scrollTop / maxScroll) * maxThumbTop : 0
    setThumb({ visible: true, height: thumbH, top })
  }, [targetRef])

  useEffect(() => {
    const el = targetRef.current
    if (!el) return
    recalc()
    el.addEventListener('scroll', recalc, { passive: true })
    window.addEventListener('resize', recalc)

    const ro = new ResizeObserver(recalc)
    ro.observe(el)
    const inner = el.querySelector('.form-content-inner')
    if (inner) ro.observe(inner)

    return () => {
      el.removeEventListener('scroll', recalc)
      window.removeEventListener('resize', recalc)
      ro.disconnect()
    }
  }, [targetRef, recalc])

  // Drag the thumb. Handlers are created per-press so add/remove share one ref.
  const onThumbMouseDown = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const el = targetRef.current
    const track = trackRef.current
    const thumbEl = thumbRef.current
    if (!el || !track || !thumbEl) return

    const startY = e.clientY
    const startScrollTop = el.scrollTop
    const thumbH = thumbEl.offsetHeight
    const maxThumbTop = track.clientHeight - thumbH
    const maxScroll = el.scrollHeight - el.clientHeight

    const onMove = (ev) => {
      if (maxThumbTop <= 0) return
      const deltaY = ev.clientY - startY
      el.scrollTop = startScrollTop + (deltaY / maxThumbTop) * maxScroll
    }
    const onUp = () => {
      document.body.style.userSelect = ''
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  // Click the track above/below the thumb → jump to that position.
  const onTrackMouseDown = (e) => {
    if (e.target === thumbRef.current) return
    const el = targetRef.current
    const track = trackRef.current
    const thumbEl = thumbRef.current
    if (!el || !track || !thumbEl) return
    const rect = track.getBoundingClientRect()
    const thumbH = thumbEl.offsetHeight
    const clickY = e.clientY - rect.top
    const maxScroll = el.scrollHeight - el.clientHeight
    const maxThumbTop = track.clientHeight - thumbH
    const target = maxThumbTop > 0
      ? ((clickY - thumbH / 2) / maxThumbTop) * maxScroll
      : 0
    el.scrollTo({
      top: Math.max(0, Math.min(maxScroll, target)),
      behavior: 'smooth',
    })
  }

  return (
    <div
      ref={trackRef}
      className={`form-scrollbar${thumb.visible ? ' is-visible' : ''}`}
      onMouseDown={onTrackMouseDown}
      aria-hidden="true"
    >
      <div
        ref={thumbRef}
        className="form-scrollbar-thumb"
        style={{
          height: `${thumb.height}px`,
          transform: `translateY(${thumb.top}px)`,
        }}
        onMouseDown={onThumbMouseDown}
      />
    </div>
  )
}
